import { Injectable } from '@angular/core';
import { SocketMiddlewareService } from '../socket-middleware.service';
import { Subject, takeUntil } from 'rxjs';
import { Graph } from 'icreate/common/models/graphs';
import { GraphsService } from './graphs.service';
import { VesselService } from '../vessel.service';
import { ExhaustGasGraphEvents, GaugeGraphEvents, GraphsEvents } from 'icreate/common/io-event/websocket-message';
import { MessageDialogService } from '@service/message-dialog.service';

@Injectable({
    providedIn: 'root',
})
export class GraphsSocketService {
    private destroySubscriptions$: Subject<void> = new Subject();

    constructor(
        private socketService: SocketMiddlewareService,
        private graphsService: GraphsService,
        private vesselService: VesselService,
        private messageDialogService: MessageDialogService
    ) {}

    graphCreated$() {
        return this.socketService.receiveMessageObserver(GraphsEvents.GRAPH_CREATED);
    }

    graphDeleted$() {
        return this.socketService.receiveMessageObserver(GraphsEvents.GRAPH_DELETED);
    }

    graphUpdated$() {
        return this.socketService.receiveMessageObserver(GraphsEvents.GRAPH_UPDATED);
    }

    graphReordered$() {
        return this.socketService.receiveMessageObserver(GraphsEvents.GRAPH_REORDERED);
    }

    startSubscriptions() {
        this.destroySubscriptions();
        this.destroySubscriptions$ = new Subject();

        this.graphCreated$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe((newGraph: Graph) => {
                const currentVesselId = this.vesselService.getActiveVesselId();
                if (newGraph.vesselId !== currentVesselId) return;
                const graphList: Graph[] = this.graphsService.getGraphList();
                if (!graphList.some(graph => graph.id === newGraph.id)) {
                    graphList.push(newGraph);
                    this.graphsService.updateGraphList(graphList);
                }
            });

        this.graphDeleted$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async ({ graphId }) => {
                const graphList: Graph[] = this.graphsService.getGraphList();
                const graphIndex = graphList.findIndex(v => v.id === graphId);
                if (graphIndex !== -1) {
                    const graph = graphList[graphIndex];
                    const title = graph.title;

                    graphList.splice(graphIndex, 1);
                    this.graphsService.updateGraphList(graphList);

                    this.messageDialogService.openMessage('graphs.message-dialog.delete.message', {
                        title,
                    });
                }
            });

        this.graphUpdated$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async ({ graphId, title }) => {
                const graphList: Graph[] = this.graphsService.getGraphList();
                let found = false;
                graphList.forEach((graph, index) => {
                    if (graph.id === graphId) {
                        graphList[index].title = title;
                        found = true;
                    }
                });
                if (found) {
                    this.graphsService.updateGraphList(graphList);
                }
            });

        this.graphReordered$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async ({ vesselId, orderedIds }) => {
                const currentVesselId = this.vesselService.getActiveVesselId();
                if (vesselId !== currentVesselId) return;
                const graphList: Graph[] = this.graphsService.getGraphList();
                const reorderedList: Graph[] = [];
                orderedIds.forEach(id => {
                    const graph = graphList.find(g => g.id === id);
                    if (graph) reorderedList.push(graph);
                });
                this.graphsService.updateGraphList(reorderedList);
            });
    }

    destroySubscriptions() {
        this.destroySubscriptions$.next();
        this.destroySubscriptions$.complete();
    }
}
